import { IPart, IPartCategoryDefault, IPartImage } from "../models/Part";
import { DateTime } from "luxon";
import { FormatType, IEbayOffer, IPartAspect } from "../models/EbayOffer";
import { computeEbayListingTitle } from "../components/ebayListings/EbayListingTitle";
import { resolveEffectiveAutoAcceptPrice } from "./bestOfferPricing";

/** Only `ebay` matters here - the other marketplaces add no readiness rules of their own. */
export type checkMarketplaces = { ebay: boolean };

type checkEbayDisabledProps = {
  part: IPart;
  offer?: IEbayOffer | null;
  partCategoryDefault?: IPartCategoryDefault | null;
  ignoreListingTime?: boolean;
  /**
   * Which marketplaces the part is being listed to. eBay-only rules (category, aspects,
   * policies, location, auction/best offer, scheduling) are skipped when ebay is false, so a
   * Shopify-only listing isn't blocked by eBay setup. Defaults to eBay for callers showing
   * general eBay readiness.
   */
  marketplaces?: checkMarketplaces;
  /**
   * How many images this part may send to eBay. Callers that know how many end slates are active
   * pass the reduced allowance, since the slates are appended to every listing and count against
   * the same cap. Mirrors PartOfferService.GetMaxListingImagesPerPart on the server.
   */
  maxListingImages?: number;
};

/**
 * How many images an eBay listing takes. 24 is the cap for single-variation listings, which is all
 * Wilma publishes. It was 12 - the multi-SKU cap - while watermarked copies were sending every
 * photo twice; that duplication is fixed, so the halved cap went with it.
 * Mirrors PartImageService.EbayImageLimit - keep the two in step.
 */
export const EBAY_IMAGE_LIMIT = 24;

/**
 * How many images the listing path would actually send for a part: the selected ones when any are
 * selected, otherwise one per photo. A watermarked or background-removed copy is the same photo as
 * the image it came from, so it is only counted once. Mirrors PartImageService.CountListingImages.
 */
export function countListingImages(images: IPartImage[]): number {
  const candidates = (images ?? []).filter((img) => !img.deleted && !img.isEndSlate);
  if (candidates.length === 0) return 0;

  const selected = candidates.filter((img) => img.useOnEbay);
  const counted = selected.length > 0 ? selected : candidates;

  //Group each copy onto the image it came from so a pair only counts once
  const byId = new Map(counted.map((img) => [img.id, img]));
  const roots = new Set<string | undefined>();

  counted.forEach((img) => {
    let current = img;
    const seen = new Set<string | undefined>();

    while (current.originalImageId && !seen.has(current.originalImageId)) {
      seen.add(current.originalImageId);
      const parent = byId.get(current.originalImageId);

      //The original is routinely missing here - it is deleted when its copy is made - but its id
      //is still the key its copies share
      if (!parent) return roots.add(current.originalImageId);

      current = parent;
    }

    roots.add(current.id);
  });

  return roots.size;
}

export type ebayChecks = {
  /** All failing checks. Prefer this when surfacing multiple reasons. */
  errors: string[];
  /** Semicolon-joined errors for callers that expect a single string. */
  error: string;
  isDisabled: boolean;
  allowEdit: boolean;
};

function buildResult(errors: string[], allowEdit: boolean): ebayChecks {
  return {
    errors,
    error: errors.join("; "),
    isDisabled: errors.length > 0,
    allowEdit: errors.length > 0 ? allowEdit : false,
  };
}

export default function checkEbayDisabled({ part, offer, partCategoryDefault, ignoreListingTime = false, marketplaces = { ebay: true }, maxListingImages = EBAY_IMAGE_LIMIT }: checkEbayDisabledProps) {
  const listingToEbay = marketplaces.ebay;

  const errors: string[] = [];
  // Soft failures (policies/prices/etc.) set this so UI can offer an edit path.
  // Hard failures (sold/imported/no images) clear it again.
  let allowEdit = true;
  let hardBlock = false;

  const addHard = (message: string) => {
    errors.push(message);
    hardBlock = true;
    allowEdit = false;
  };

  const addSoft = (message: string) => {
    errors.push(message);
  };

  if (part.soldDate) {
    addHard("This part has been sold and cannot be edited.");
  }

  if (!part.categoryId) {
    addHard("Part category must be set.");
  }

  if (offer && part.offers.length === 0 && !partCategoryDefault) {
    addHard("This part has no listings created.");
  }

  // Imported parts can't be relisted - Idea being they've been published before but we don't have the ebay IDs to update the listing, we have syncs now so this shouldn't ever be an issue
  if (listingToEbay && part.offers.length > 0 && part.offers[0].publishedOn !== null && part.offers[0].ebayId === null && part.ebayInventoryApiOfferId == null && part.ebayTradingApiItemId == null) {
    addHard("This part has been imported and cannot be edited.");
  }

  // Title validation
  if (offer && (!offer.title || offer.title.length < 1 || offer.title.length > 80)) {
    addHard("Title must be between 1 and 80 characters.");
  }

  if (!offer && partCategoryDefault && !partCategoryDefault.titleStructure) {
    addHard("Title must be between 1 and 80 characters or default structure must be set.");
  }

  if (!offer && partCategoryDefault && partCategoryDefault.titleStructure) {
    var title = computeEbayListingTitle(part);

    if (title && (title?.length < 1 || title?.length > 80)) {
      addHard("Title must be between 1 and 80 characters.");
    }
  }

  // Need at least thumbnail
  if (part.images.length === 0) {
    addHard("You must upload at least one image.");
  }

  // If none of them are set to use on eBay, treat all of them as selected for eBay
  // No validation error needed - we'll treat all images as selected when publishing

  // eBay caps how many images a listing takes, and publishing used to silently drop the overflow -
  // which is how parts went live without the damage shots at the end of the set. Block instead so
  // the images get picked. Soft, because deselecting some is an edit the user can make from here.
  if (listingToEbay) {
    const listingImageCount = countListingImages(part.images);

    if (listingImageCount > maxListingImages) {
      addSoft(`Too many images selected for eBay - ${listingImageCount} selected, ${maxListingImages} allowed.`);
    }
  }

  // eBay only - the 3 week window is an eBay scheduling limit.
  if (listingToEbay && ignoreListingTime === false && offer && !offer.listNow) {
    if (!offer.listingStarted) {
      addSoft("Listing start date must be set.");
    } else {
      // Make sure listingStarted is after now
      if (DateTime.fromISO(offer.listingStarted) < DateTime.now()) {
        addSoft("Listing start date must be in the future.");
      }

      // Listing has to start within 3 weeks
      if (DateTime.fromISO(offer.listingStarted) > DateTime.now().plus({ weeks: 3 })) {
        addSoft("Listing start date must be within 3 weeks.");
      }
    }
  }

  // Use the appropriate object for format checks
  const formatObject = offer || partCategoryDefault;
  if (!formatObject) {
    addHard("No valid offer or part category default provided.");
  } else if (formatObject.format === FormatType.Auction && listingToEbay) {
    // eBay only - no other marketplace runs auctions.
    // Make sure auction start price is set
    if ((!formatObject.auctionStartPrice || formatObject.auctionStartPrice <= 0) || (!part.enteredPrice || part.enteredPrice <= 0)) {
      addSoft("Auction start price must be set.");
    }
    // Make sure auction reserve price is set
    if (!formatObject.auctionReservePrice || formatObject.auctionReservePrice <= 0) {
      addSoft("Auction reserve price must be set.");
    }

    // Make sure auction start price is less than reserve price
    if (formatObject.auctionStartPrice && formatObject.auctionReservePrice && formatObject.auctionStartPrice > formatObject.auctionReservePrice) {
      addSoft("Auction start price must be less than reserve price.");
    }
  } else if (formatObject.format === FormatType.FixedPrice || !listingToEbay) {
    // Non-eBay marketplaces sell at the buy it now price whatever the format says, so they
    // always take this branch rather than falling through the auction check above unvalidated.
    // Check pricing - use offer price if available, otherwise check part.enteredPrice
    let hasValidPrice = false;
    if (offer) {
      hasValidPrice = !!(offer.buyItNowPrice && offer.buyItNowPrice > 0);
    } else {
      hasValidPrice = !!(part.enteredPrice && part.enteredPrice > 0);
    }

    if (!hasValidPrice) {
      addHard("Buy it now price must be set.");
    }

    // A missing auto accept price is no longer a blocker: the backend always resolves one
    // (explicit price > category threshold % > global default %) when Best Offer is enabled and a
    // Buy It Now price exists. The only genuinely blocking case (no BIN) is already handled above.
    const buyItNowPrice = offer?.buyItNowPrice || part.enteredPrice || 0;

    // Make sure the effective best offer price is lower than the buy it now price (eBay only)
    if (listingToEbay && formatObject.enableBestOffer) {
      const effectiveAutoAcceptPrice = resolveEffectiveAutoAcceptPrice(
        buyItNowPrice,
        formatObject.autoAcceptPrice,
        partCategoryDefault?.autoAcceptThreshold,
      );

      if (effectiveAutoAcceptPrice && effectiveAutoAcceptPrice >= buyItNowPrice) {
        addSoft("Best offer price must be lower than buy it now price.");
      }
    }
  }

  // Policies and location are eBay business policies - nothing else uses them. Publish sends the
  // offer's own, else the category default's, field by field (PartOfferService.ResolveEbayListingSetup),
  // so only a field missing from both blocks. Checking the offer alone flagged "Can't relist" on parts
  // that relist fine.
  if (listingToEbay && formatObject) {
    const resolve = (own?: string | null, fallback?: string | null) => (own?.trim() ? own : fallback?.trim() ? fallback : null);

    if (!resolve(offer?.fulfillmentPolicyId, partCategoryDefault?.fulfillmentPolicyId)) {
      addSoft("Fulfillment policy must be set.");
    }

    if (!resolve(offer?.paymentPolicyId, partCategoryDefault?.paymentPolicyId)) {
      addSoft("Payment policy must be set.");
    }

    if (!resolve(offer?.returnPolicyId, partCategoryDefault?.returnPolicyId)) {
      addSoft("Return policy must be set.");
    }

    if (!resolve(offer?.locationKey, partCategoryDefault?.locationKey)) {
      addSoft("Location must be set.");
    }
  }

  // eBay category and item aspects are eBay taxonomy - a Shopify-only listing needs neither.
  if (listingToEbay) {
    const hasCategoryId = offer ? offer.categoryId : (partCategoryDefault ? partCategoryDefault.ebayCategoryId : null);

    if (!hasCategoryId) {
      addSoft("eBay category needs to be set.");
    }

    // If we have an offer, check to make sure we have a value in every required aspect
    // Else check the partCategoryDefault for required aspects
    let requiredAspects: IPartAspect[] = [];

    if (offer) {
      requiredAspects = part.aspects?.filter(aspect => aspect.isRequired);
    } else if (partCategoryDefault) {
      requiredAspects = partCategoryDefault.categoryCustomAspects?.filter(aspect => aspect.isRequired);
    }

    if (requiredAspects && requiredAspects.length > 0) {
      const missingAspects = requiredAspects?.filter((aspect: IPartAspect) => !aspect.value || aspect.value.length === 0);
      if (missingAspects && missingAspects.length > 0) {
        addHard(`Missing required aspects: ${missingAspects.map(a => a.name).join(", ")}`);
      }
    }
  }

  return buildResult(errors, hardBlock ? false : allowEdit && errors.length > 0);
}
